import React, { useState } from 'react';

export default function ContactForm({ defaultSubject = '', formTitle = 'Send os en besked' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    subject: defaultSubject || 'Generel henvendelse',
    message: '',
    website_url: '' // Honeypot
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    // Antispam check
    if (formData.website_url) {
      // Bot detected silently succeed
      setStatus('success');
      return;
    }

    // Basic client validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setStatus('error');
      setErrorMessage('Udfyld venligst alle påkrævede felter.');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        website_url: formData.website_url
      };

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          subject: defaultSubject || 'Generel henvendelse',
          message: '',
          website_url: ''
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setStatus('error');
        setErrorMessage(
          errorData.message || 'Der opstod en fejl under afsendelsen. Prøv venligst igen eller ring til os.'
        );
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage('Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse eller kontakt os direkte på telefon.');
    }
  };

  return (
    <div className="bg-white rounded-3xl p-6 sm:p-8 lg:p-10 border border-[#d9e5d4] shadow-sm">
      <div className="mb-6">
        <h3 className="text-2xl font-bold text-[#1D2923] font-serif-heading">
          {formTitle}
        </h3>
        <p className="text-sm text-[#5A6961] mt-1">
          Udfyld formularen, så vender vi hurtigt tilbage til dig.
        </p>
      </div>

      {status === 'success' ? (
        <div className="bg-[#264A35]/10 border border-[#264A35]/20 rounded-2xl p-6 text-center animate-fadeIn">
          <div className="w-14 h-14 mx-auto rounded-full bg-[#264A35] text-white flex items-center justify-center mb-4 text-2xl shadow-sm">
            <i className="fa-solid fa-check" aria-hidden="true" />
          </div>
          <h4 className="text-xl font-bold text-[#1D2923] mb-2 font-serif-heading">
            Tak for din henvendelse!
          </h4>
          <p className="text-sm text-[#3E4E45] leading-relaxed max-w-md mx-auto">
            Vi har modtaget din besked og kontakter dig snarest muligt. Du er også altid velkommen til at ringe til os på <strong>86 54 38 66</strong>.
          </p>
          <button
            type="button"
            onClick={() => setStatus('idle')}
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-[#264A35] bg-white hover:bg-[#f8faf5] border border-[#d9e5d4] transition-colors"
          >
            <span>Send en ny besked</span>
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          
          {/* Honeypot field for antispam */}
          <div className="sr-only" aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
            <label htmlFor="website_url">Hjemmesideadresse</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              tabIndex={-1}
              autoComplete="off"
              value={formData.website_url}
              onChange={handleChange}
            />
          </div>

          {status === 'error' && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-3">
              <i className="fa-solid fa-circle-exclamation mt-0.5" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Name */}
          <div>
            <label htmlFor="form-name" className="block text-xs font-bold uppercase tracking-wider text-[#2D3A32] mb-1.5">
              Dit Fulde Navn <span className="text-[#C85A32]">*</span>
            </label>
            <input
              type="text"
              id="form-name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="F.eks. Mette Hansen"
              className="w-full px-4 py-3 rounded-xl border border-[#D8CEBF] bg-white text-[#1D2923] placeholder-[#9CB3A5] focus:outline-none focus:ring-2 focus:ring-[#264A35] focus:border-transparent text-sm transition-all"
            />
          </div>

          {/* Phone and Email in 2 columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="form-phone" className="block text-xs font-bold uppercase tracking-wider text-[#2D3A32] mb-1.5">
                Telefonnummer <span className="text-[#C85A32]">*</span>
              </label>
              <input
                type="tel"
                id="form-phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 12 34 56 78"
                className="w-full px-4 py-3 rounded-xl border border-[#D8CEBF] bg-white text-[#1D2923] placeholder-[#9CB3A5] focus:outline-none focus:ring-2 focus:ring-[#264A35] focus:border-transparent text-sm transition-all"
              />
            </div>

            <div>
              <label htmlFor="form-email" className="block text-xs font-bold uppercase tracking-wider text-[#2D3A32] mb-1.5">
                E-mailadresse <span className="text-[#C85A32]">*</span>
              </label>
              <input
                type="email"
                id="form-email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="F.eks. foraelder@email.dk"
                className="w-full px-4 py-3 rounded-xl border border-[#D8CEBF] bg-white text-[#1D2923] placeholder-[#9CB3A5] focus:outline-none focus:ring-2 focus:ring-[#264A35] focus:border-transparent text-sm transition-all"
              />
            </div>
          </div>

          {/* Subject */}
          <div>
            <label htmlFor="form-subject" className="block text-xs font-bold uppercase tracking-wider text-[#2D3A32] mb-1.5">
              Hvad drejer henvendelsen sig om?
            </label>
            <select
              id="form-subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-[#D8CEBF] bg-white text-[#1D2923] focus:outline-none focus:ring-2 focus:ring-[#264A35] focus:border-transparent text-sm transition-all"
            >
              <option value="Generel henvendelse">Generel henvendelse</option>
              <option value="Besøg skolen / Rundvisning">Besøg skolen / Rundvisning</option>
              <option value="Opskrivning til venteliste">Opskrivning til venteliste</option>
              <option value="Spørgsmål til optagelse">Spørgsmål til optagelse</option>
              <option value="Fritteren & Klub">Fritteren & Klub</option>
              <option value="Andet">Andet</option>
            </select>
          </div>

          {/* Message */}
          <div>
            <label htmlFor="form-message" className="block text-xs font-bold uppercase tracking-wider text-[#2D3A32] mb-1.5">
              Beskrivelse / Besked <span className="text-[#C85A32]">*</span>
            </label>
            <textarea
              id="form-message"
              name="message"
              rows={4}
              required
              value={formData.message}
              onChange={handleChange}
              placeholder="Skriv om dit barn, klassetrin eller hvad vi kan hjælpe med..."
              className="w-full px-4 py-3 rounded-xl border border-[#D8CEBF] bg-white text-[#1D2923] placeholder-[#9CB3A5] focus:outline-none focus:ring-2 focus:ring-[#264A35] focus:border-transparent text-sm transition-all resize-y"
            />
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-base font-bold text-[#1D2923] bg-[#c1d8e5] hover:bg-[#aecad9] shadow-sm hover:shadow transition-all disabled:opacity-60 cursor-pointer"
          >
            {status === 'loading' ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                <span>Sender besked...</span>
              </>
            ) : (
              <>
                <span>Send henvendelse</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
